import { useState, useEffect, useCallback } from "react"
import { Button, Spinner, Badge } from "reactstrap"
import { toast } from "react-toastify"
import ThemeModal from "./ThemeModal"
import {
  getGoogleDriveStatus,
  getGoogleDriveAuthUrl,
  syncGoogleDriveExcel,
  disconnectGoogleDrive,
} from "api/stockApi"

const DriveSyncModal = ({ isOpen, toggle }) => {
  const [connected, setConnected] = useState(false)
  const [email, setEmail] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [syncResult, setSyncResult] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(false)

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true)
    try {
      const res = await getGoogleDriveStatus()
      setConnected(res?.data?.connected ?? false)
      setEmail(res?.data?.email ?? null)
    } catch {
      setConnected(false)
      setEmail(null)
    } finally {
      setLoadingStatus(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchStatus()
      setSyncResult(null)
    }
  }, [isOpen, fetchStatus])

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      const res = await getGoogleDriveAuthUrl()
      const url = res?.data?.url

      if (!url) {
        toast.error("Failed to get Google authorization URL.")
        return
      }

      const popup = window.open(
        url,
        "google-oauth",
        "width=500,height=600,scrollbars=yes"
      )

      // Poll status while popup is open
      let attempts = 0
      const maxAttempts = 30
      const pollInterval = setInterval(async () => {
        attempts++
        if (popup && popup.closed) {
          clearInterval(pollInterval)
          setIsConnecting(false)
          fetchStatus()
          return
        }
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval)
          setIsConnecting(false)
          fetchStatus()
          return
        }
        try {
          const statusRes = await getGoogleDriveStatus()
          if (statusRes?.data?.connected) {
            clearInterval(pollInterval)
            setEmail(statusRes.data.email)
            setConnected(true)
            toast.success(`Connected as ${statusRes.data.email}`)
            setIsConnecting(false)
            if (popup && !popup.closed) popup.close()
          }
        } catch {
          // ignore polling errors
        }
      }, 1000)

      // Also listen for postMessage as fallback
      const handleMessage = event => {
        if (event.data?.success) {
          clearInterval(pollInterval)
          setEmail(event.data.email)
          setConnected(true)
          toast.success(`Connected as ${event.data.email}`)
          setIsConnecting(false)
        } else if (event.data?.error) {
          clearInterval(pollInterval)
          toast.error(`Connection failed: ${event.data.error}`)
          setIsConnecting(false)
        }
        window.removeEventListener("message", handleMessage)
      }

      window.addEventListener("message", handleMessage)
    } catch {
      toast.error("Failed to initiate Google Drive connection.")
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setIsDisconnecting(true)
    try {
      await disconnectGoogleDrive()
      setConnected(false)
      setEmail(null)
      setSyncResult(null)
      toast.success("Google Drive disconnected.")
    } catch {
      toast.error("Failed to disconnect Google Drive.")
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleSync = async () => {
    setIsSyncing(true)
    setSyncResult(null)
    try {
      const res = await syncGoogleDriveExcel()
      setSyncResult(res?.data)
      toast.success("Showroom Excel synced to Google Drive!")
    } catch {
      toast.error("Failed to sync Excel to Google Drive.")
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Showroom Excel Drive Sync"
      size="md"
      footer={
        <>
          <Button color="light" onClick={toggle}>
            Close
          </Button>
          {connected && (
            <Button
              color="success"
              onClick={handleSync}
              disabled={isSyncing || loadingStatus}
            >
              {isSyncing ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Syncing...
                </>
              ) : (
                <>
                  <i className="bx bx-cloud-upload me-1" />
                  Sync to Drive
                </>
              )}
            </Button>
          )}
        </>
      }
    >
      {/* Connection Status */}
      <div className="border rounded p-3 bg-light mb-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className="text-muted small text-uppercase">Status</div>
            <div className="d-flex align-items-center gap-2 mt-1">
              {loadingStatus ? (
                <Spinner size="sm" />
              ) : connected ? (
                <>
                  <Badge color="success">Connected</Badge>
                  <span className="text-muted small">{email}</span>
                </>
              ) : (
                <Badge color="danger">Not Connected</Badge>
              )}
            </div>
          </div>
          <div>
            {connected ? (
              <Button
                color="outline-danger"
                size="sm"
                onClick={handleDisconnect}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? (
                  <Spinner size="sm" />
                ) : (
                  "Disconnect"
                )}
              </Button>
            ) : (
              <Button
                color="primary"
                size="sm"
                onClick={handleConnect}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-link me-1" />
                    Connect Gmail
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Sync Result */}
      {syncResult && (
        <div className="border rounded p-3 bg-light mb-3">
          <div className="text-muted small text-uppercase">Last Sync</div>
          <div className="mt-1">
            <div className="fw-medium">{syncResult?.file?.name}</div>
            <a
              href={syncResult?.file?.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary small"
            >
              <i className="bx bx-link-external me-1" />
              View on Google Drive
            </a>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div>
        <h6 className="mb-2">How it works?</h6>
        <ul className="mb-0 ps-3 small text-muted">
          <li>Click "Connect Gmail" to link your Google account.</li>
          <li>
            The showroom stock Excel will be uploaded to{" "}
            <strong>/Aurobil Cars/</strong> folder on your Google Drive.
          </li>
          <li>Each sync overwrites the previous file with the latest data.</li>
          <li>
            You can disconnect anytime to remove stored credentials.
          </li>
        </ul>
      </div>

      {/* Syncing overlay */}
      {isSyncing && (
        <div className="text-center py-3">
          <Spinner color="primary" />
          <div className="mt-2 text-muted small">
            Generating Excel and uploading to Google Drive...
          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default DriveSyncModal
